'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Globe, Loader2, MapPin } from 'lucide-react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { CountrySelector } from '../../country/country-selector';
import {
  DEFAULT_ADDRESS_VALUE,
  type AddressFieldError,
  type AddressInputProps,
  type AddressValue,
} from './types';
import { usePlaces } from './use-places';
import { AddressInputView } from './view';

// Re-export schema and types so callers can:
//   import { AddressInput, AddressSchema, type AddressValue } from '@kit/artists';
// (Stretch criterion 2 in WIP_consolidating_address_input_component_2026_05_22.)
export { AddressSchema, type AddressInfo, type AddressValue } from './types';
export {
  ADDRESS_AUTOCOMPLETE_COUNTRIES,
  PLACES_CALLBACK_TIMEOUT_MS,
  PLACES_FAIL_OPEN_AFTER_CONSECUTIVE_TIMEOUTS,
} from './use-places';

/**
 * Address picker with built-in Google Places autocomplete. Self-wired
 * via react-hook-form: callers pass `name` and the component reads
 * value/onChange/error from `useFormContext()`.
 *
 * The parent form schema MUST shape the slot at `name` as
 * {@link AddressValue} (re-export `AddressSchema` from this module to
 * keep schemas in sync).
 *
 * Hardenings carried over from the legacy single-file component:
 * - 5s timeout on `getDetails` and `getPredictions`
 * - 350ms debounce via `useDebouncedCallback`
 * - Fail-open manual-entry hint after 2 consecutive timeouts (or when
 *   the API key is missing / Google Maps fails to load)
 * - Multi-country `ADDRESS_AUTOCOMPLETE_COUNTRIES` allowlist
 * - `place_id` capture into `value.placeId` (cleared on free-text typing)
 * - `address-line1` / `address-level1` / `address-level2` / `postal-code` autoComplete tokens
 * - Per-field onBlur DOM sync (recovers Chrome autofill writes that
 *   bypass React's `onChange` handler)
 * - `onAutocompleteError` for Sentry forwarding
 */
export function AddressInput(props: AddressInputProps) {
  if (props.mode === 'view') {
    return <AddressInputViewWrapper {...props} />;
  }
  return <AddressInputEditor {...props} />;
}

// ---------------------------------------------------------------------------
// View mode
// ---------------------------------------------------------------------------

function AddressInputViewWrapper(props: AddressInputProps) {
  const { name, className, label, hint } = props;

  // Throws a clear error if rendered outside FormProvider.
  const ctx = useFormContext();
  assertFormContext(ctx, name);

  const { field } = useController<Record<string, AddressValue>>({
    name,
    control: ctx.control,
  });

  return (
    <AddressInputView
      value={(field.value ?? DEFAULT_ADDRESS_VALUE) as AddressValue}
      className={className}
      label={label}
      hint={hint}
    />
  );
}

// ---------------------------------------------------------------------------
// Edit mode
// ---------------------------------------------------------------------------

function AddressInputEditor({
  name,
  required = false,
  className,
  label,
  hint,
  apiKey = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
  onAutocompleteError,
}: AddressInputProps) {
  const { t } = useTranslation();
  const ctx = useFormContext();
  assertFormContext(ctx, name);

  const resolvedLabel = label ?? t('dancer:addressInput.label');

  const { field, fieldState, formState } = useController<
    Record<string, AddressValue>
  >({
    name,
    control: ctx.control,
  });

  const value: AddressValue = field.value ?? DEFAULT_ADDRESS_VALUE;
  const errors: AddressFieldError = extractFieldErrors(formState, name);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Close the suggestions dropdown when clicking outside.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const places = usePlaces({
    apiKey,
    pickedCountryCode: value.countryCode,
    onError: onAutocompleteError ?? (() => {}),
  });

  const commit = useCallback(
    (next: AddressValue) => {
      field.onChange(next);
    },
    [field],
  );

  // -------------------------------------------------------------------------
  // Field handlers
  // -------------------------------------------------------------------------

  const handleStreetAddressChange = useCallback(
    (newValue: string) => {
      commit({
        ...value,
        streetAddress: newValue,
        // Typing after a Places selection invalidates the captured place_id.
        placeId: undefined,
      });

      // Skip autocomplete entirely when we've already declared it dead,
      // so the user can finish typing without re-arming the spinner.
      if (apiKey && newValue.length > 2 && !places.autocompleteFailed) {
        setShowSuggestions(true);
        places.getPlacePredictions(newValue);
      } else {
        setShowSuggestions(false);
      }
    },
    [apiKey, commit, places, value],
  );

  const updateField = useCallback(
    <K extends keyof AddressValue>(fieldKey: K, newValue: AddressValue[K]) => {
      commit({ ...value, [fieldKey]: newValue });
    },
    [commit, value],
  );

  /**
   * Sync the input's actual DOM value into form state on blur.
   *
   * Why this exists: Chrome's saved-address autofill writes the value
   * directly to the DOM `input` element WITHOUT firing the React
   * `onChange` handler. The user sees the autofilled text in the
   * field, but the controlled form state stays empty — so the
   * wizard's `missingFields` check still flags Street Address /
   * Postal Code as empty, the Continue button stays disabled, and the
   * dancer can't advance.
   *
   * On blur we read the DOM value and force-sync it through the
   * normal onChange path. If the value already matches form state
   * this is a no-op; if Chrome autofilled, this is the recovery.
   *
   * Observed in production 2026-05-22 with Jieun Aurora — visible
   * field values that the form treated as empty. The `autoComplete`
   * tokens below (added at the same time) also help by routing
   * autofill through events React DOES catch on most browsers, but
   * the onBlur sync is the belt-and-suspenders fix.
   */
  const syncFromDOMOnBlur = useCallback(
    (fieldKey: keyof AddressValue) =>
      (e: React.FocusEvent<HTMLInputElement>) => {
        const domValue = e.target.value;
        const stateValue = (value[fieldKey] ?? '') as string;
        if (domValue !== stateValue) {
          if (fieldKey === 'streetAddress') {
            handleStreetAddressChange(domValue);
          } else {
            updateField(fieldKey, domValue as AddressValue[typeof fieldKey]);
          }
        }
        field.onBlur();
      },
    [field, handleStreetAddressChange, updateField, value],
  );

  const handlePlaceSelect = useCallback(
    async (placeId: string) => {
      const resolved = await places.resolvePlaceDetails(placeId);
      if (!resolved) return;
      commit({
        streetAddress: resolved.streetAddress,
        // Keep the apartment the user typed; Google doesn't return it.
        apartment: value.apartment ?? '',
        city: resolved.city,
        stateProvince: resolved.stateProvince,
        postalCode: resolved.postalCode,
        countryCode: resolved.countryCode,
        placeId: resolved.placeId,
      });
      setShowSuggestions(false);
      places.clearPredictions();
    },
    [commit, places, value.apartment],
  );

  // -------------------------------------------------------------------------
  // Render — note: structure intentionally mirrors the legacy component
  // (same scroll-target ids, same a11y labels, same Tailwind classes) so
  // the unit + e2e specs continue to pass without selector churn.
  // -------------------------------------------------------------------------

  const showSpinner = places.isPredictionsLoading || places.isLoadingPlace;

  return (
    <Card className={cn('p-3 sm:p-4', className)}>
      <CardContent className="space-y-3 p-0 sm:space-y-4">
        {/* Header */}
        <div className="mb-4">
          <div className="mb-1 flex items-center gap-2">
            <MapPin className="text-muted-foreground h-4 w-4" />
            <Label className="text-sm font-medium">{resolvedLabel}</Label>
          </div>
          {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        </div>

        {/* Country */}
        <div className="space-y-1" id="address-field-countryCode">
          <Label className="flex items-center gap-1 text-xs">
            <Globe className="h-3 w-3" />
            <Trans i18nKey="dancer:addressInput.country" />{' '}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <CountrySelector
            value={value.countryCode || 'CH'}
            onValueChange={(code: string) => updateField('countryCode', code)}
            error={errors.countryCode}
          />
          {errors.countryCode && (
            <p className="text-destructive mt-1 text-xs">
              {errors.countryCode}
            </p>
          )}
        </div>

        {/* Street Address + autocomplete */}
        <div className="relative space-y-1" id="address-field-streetAddress">
          <Label className="text-xs">
            <Trans i18nKey="dancer:addressInput.streetAddress" />{' '}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <div className="relative">
            <Input
              placeholder={t('dancer:addressInput.streetPlaceholder')}
              value={value.streetAddress || ''}
              onChange={(e) => handleStreetAddressChange(e.target.value)}
              onBlur={syncFromDOMOnBlur('streetAddress')}
              className={cn(
                'h-11',
                errors.streetAddress &&
                  'border-destructive focus:ring-destructive',
                showSpinner && 'pr-10',
              )}
              // `address-line1` lets Chrome autofill through a real
              // autofill event that React DOES catch (autoComplete="off"
              // is ignored by Chrome for address fields). The onBlur
              // sync above is the recovery if autofill still slips past
              // React's reconciler.
              autoComplete="address-line1"
            />
            {showSpinner && (
              <Loader2
                aria-hidden
                data-testid="address-autocomplete-spinner"
                className="text-muted-foreground absolute top-3 right-3 h-4 w-4 animate-spin"
              />
            )}
          </div>
          {errors.streetAddress && (
            <p className="text-destructive mt-1 text-xs">
              {errors.streetAddress}
            </p>
          )}
          {places.autocompleteFailed && (
            <p
              data-testid="address-autocomplete-failed-hint"
              className="text-muted-foreground mt-1 flex items-start gap-1 text-xs"
            >
              <AlertCircle className="mt-[2px] h-3 w-3 flex-shrink-0" />
              <span>
                <Trans i18nKey="dancer:addressInput.autocompleteUnavailable" />
              </span>
            </p>
          )}

          {showSuggestions && places.placePredictions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-lg"
            >
              {places.placePredictions.map((prediction) => (
                <Button
                  key={prediction.place_id}
                  type="button"
                  variant="ghost"
                  className="hover:bg-accent hover:text-accent-foreground border-border flex min-h-[44px] w-full flex-col items-stretch justify-center rounded-none border-b px-4 py-3 text-left text-sm transition-colors last:border-b-0"
                  onClick={() => handlePlaceSelect(prediction.place_id)}
                >
                  <div className="font-medium">
                    {prediction.structured_formatting.main_text}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Apartment */}
        <div className="space-y-1" id="address-field-apartment">
          <Label className="text-xs">
            <Trans i18nKey="dancer:addressInput.apartment" />
          </Label>
          <Input
            placeholder={t('dancer:addressInput.apartmentPlaceholder')}
            value={value.apartment || ''}
            onChange={(e) => updateField('apartment', e.target.value)}
            onBlur={syncFromDOMOnBlur('apartment')}
            className={cn(
              'h-11',
              errors.apartment && 'border-destructive focus:ring-destructive',
            )}
            autoComplete="address-line2"
          />
          {errors.apartment && (
            <p className="text-destructive mt-1 text-xs">{errors.apartment}</p>
          )}
        </div>

        {/* City / State row */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1" id="address-field-city">
            <Label className="text-xs">
              <Trans i18nKey="dancer:addressInput.city" />{' '}
              {required && <span className="text-destructive">*</span>}
            </Label>
            <Input
              placeholder={t('dancer:addressInput.cityPlaceholder')}
              value={value.city || ''}
              onChange={(e) => updateField('city', e.target.value)}
              onBlur={syncFromDOMOnBlur('city')}
              className={cn(
                'h-11',
                errors.city && 'border-destructive focus:ring-destructive',
              )}
              autoComplete="address-level2"
            />
            {errors.city && (
              <p className="text-destructive mt-1 text-xs">{errors.city}</p>
            )}
          </div>

          <div className="space-y-1" id="address-field-stateProvince">
            <Label className="text-xs">
              <Trans i18nKey="dancer:addressInput.stateProvince" />
            </Label>
            <Input
              placeholder={t('dancer:addressInput.stateProvincePlaceholder')}
              value={value.stateProvince || ''}
              onChange={(e) => updateField('stateProvince', e.target.value)}
              onBlur={syncFromDOMOnBlur('stateProvince')}
              className={cn(
                'h-11',
                errors.stateProvince &&
                  'border-destructive focus:ring-destructive',
              )}
              autoComplete="address-level1"
            />
            {errors.stateProvince && (
              <p className="text-destructive mt-1 text-xs">
                {errors.stateProvince}
              </p>
            )}
          </div>
        </div>

        {/* Postal */}
        <div className="space-y-1" id="address-field-postalCode">
          <Label className="text-xs">
            <Trans i18nKey="dancer:addressInput.postalCode" />{' '}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            placeholder={t('dancer:addressInput.postalPlaceholder')}
            value={value.postalCode || ''}
            onChange={(e) => updateField('postalCode', e.target.value)}
            onBlur={syncFromDOMOnBlur('postalCode')}
            className={cn(
              'h-11 max-w-[200px] sm:max-w-[250px]',
              errors.postalCode && 'border-destructive focus:ring-destructive',
            )}
            autoComplete="postal-code"
          />
          {errors.postalCode && (
            <p className="text-destructive mt-1 text-xs">{errors.postalCode}</p>
          )}
        </div>

        {/* Privacy notice — only when consumer hasn't provided their own hint. */}
        {!hint && (
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-muted-foreground text-xs">
              <Trans i18nKey="dancer:addressInput.privacyNotice" />
            </p>
          </div>
        )}

        {/* fieldState.error is RHF's top-level error for the slot itself;
            sub-field errors above already render inline messages, so this is
            silent unless a parent schema attaches a refine() at the address
            object level (which today's wizard schema does not). */}
        {fieldState.error &&
          !fieldState.error.message?.startsWith('Invalid') && (
            <p className="text-destructive text-xs" role="alert">
              {fieldState.error.message}
            </p>
          )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walks `formState.errors` to extract sub-field errors for the address
 * slot. Returns an empty object when there are no errors.
 *
 * `name` is the parent RHF field path (`"address"`); we read errors at
 * `formState.errors[name].<field>?.message`.
 */
function extractFieldErrors(
  formState: { errors: Record<string, unknown> },
  name: string,
): AddressFieldError {
  const slot = formState.errors[name] as
    | Record<string, { message?: string } | undefined>
    | undefined;
  if (!slot) return {};
  return {
    streetAddress: slot.streetAddress?.message,
    apartment: slot.apartment?.message,
    city: slot.city?.message,
    stateProvince: slot.stateProvince?.message,
    postalCode: slot.postalCode?.message,
    countryCode: slot.countryCode?.message,
  };
}

/**
 * Surface a clear, greppable error when `<AddressInput>` is rendered
 * outside a `<FormProvider>`. Without this guard, `useFormContext()`
 * returns `null` and the next `useController()` call throws an
 * unintelligible "Cannot destructure property 'control' of null".
 */
function assertFormContext(
  ctx: ReturnType<typeof useFormContext> | null,
  name: string,
): asserts ctx {
  if (!ctx) {
    throw new Error(
      `<AddressInput name="${name}"> must be rendered inside a <FormProvider>. ` +
        `Wrap the parent form tree with <FormProvider {...form}> (react-hook-form).`,
    );
  }
}
